


import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useDispatch } from "react-redux";

// Adjust these import paths to match your project structure
import { useLoginMutation } from "../../../services/redux/api/baseApi";
import { setCredentials } from "../../../services/redux/slices/authSlice";
import {toast} from "react-toastify";
const LoginModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [strength, setStrength] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState(null);

  // RTK Query mutation hook
  const [login, { isLoading }] = useLoginMutation();

  if (!isOpen) return null;

  // Compute strength
  const calcStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    if (score >= 4) return "Strong";
    if (score >= 2) return "Average";
    return "Weak";
  };

  const onPasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    setStrength(calcStrength(pwd));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // basic client-side validation
    if (!email.trim() || !password) {
      setError("Please enter email and password.");
      return;
    }

    try {
      // call login mutation and unwrap to throw on error
      const res = await login({ email: email.trim(), password }).unwrap();

      // expected res shape: { token, user } — adapt if your backend differs
      const token = res.token ?? res?.data?.token;
      const user = res.user ?? res?.data?.user ?? null;

      if (!token) {
        setError("Login succeeded but no token returned by server.");
        return;
      }

      // Save credentials in Redux (authSlice saves to localStorage inside reducer)
      dispatch(setCredentials({ token, user }));

      // If user didn't check "Remember me", remove persisted auth so token only lives in memory
      // (authSlice writes to localStorage; remove it if user didn't want persistence)
      if (!rememberMe) {
        try {
          localStorage.removeItem("auth");
        } catch (err) {
          // ignore
        }
      }

      // navigate & close modal
      onClose?.();
      navigate("/life-product");
      toast.success("Logged in successfully!");
    } catch (err) {
      // RTK Query error shape varies. Try to show useful message.
      const serverMessage =
        (err?.data && (err.data.message || err.data.error)) ||
        err?.error ||
        err?.message ||
        "Login failed. Please check your credentials.";
      setError(serverMessage);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <button className="close-button" onClick={onClose} aria-label="Close">
          <div className="circle">
            <span className="x-icon">X</span>
          </div>
        </button>

        <h2 className="modal-title">Sign in</h2>
        <div className="underline" />

        <form onSubmit={handleSubmit} noValidate>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            placeholder="example.email@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />

          <label htmlFor="password">Password</label>
          <div className="password-wrapper">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter at least 8+ characters"
              value={password}
              onChange={onPasswordChange}
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {password && (
            <>
              <div className="strength-bar">
                <div
                  className={
                    strength === "Strong"
                      ? "bar strong"
                      : strength === "Average"
                      ? "bar average"
                      : "bar weak"
                  }
                />
              </div>
              <p className={`strength-text ${strength.toLowerCase()}`}>
                Password Strength: {strength}
              </p>
            </>
          )}

          <div className="remember-me" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>Remember me</span>
            </label>
            <a href="#" className="forgot-password" onClick={(ev) => ev.preventDefault()}>
              Forgot password?
            </a>
          </div>

          {error && <div className="error-text" role="alert" style={{ color: "red", marginTop: 8 }}>{error}</div>}

          <button type="submit" className="login-button" disabled={isLoading} style={{ marginTop: 12 }}>
            {isLoading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <p className="terms-text">
          By Proceeding I Agree to{" "}
          <a href="#" onClick={(ev) => ev.preventDefault()}>T&C</a> and{" "}
          <a href="#" onClick={(ev) => ev.preventDefault()}>Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default LoginModal;
